/**
 * photo-studio-launch.js
 * Ashe Design – the "Open Ashe Photo Studio" button on /pages/photo-studio.
 *
 * 1. Sends the customer's plugin key to POST /studio/handoff.
 * 2. The server checks the key (same check as Remove Background) and
 *    returns a one-time sign-in link that works once, for 60 seconds.
 * 3. The browser goes straight to that link, which signs the customer in
 *    to studio.ashedesign.com.
 */
(() => {
  'use strict';

  const REQUEST_TIMEOUT_MS = 20000;

  const MESSAGES = {
    not_authorized: 'Your login has expired. Please refresh this page and log in again.',
    revoked: "This account can't use Ashe Photo Studio. Please contact us for help.",
    network: "We couldn't reach Ashe Photo Studio. Check your internet connection and try again.",
    unknown: 'Something went wrong. Please try again in a moment.',
  };

  class LaunchError extends Error {
    constructor(code, message) {
      super(message || code);
      this.name = 'LaunchError';
      this.code = code;
    }
  }

  function readConfig(root) {
    const node = root.querySelector('[data-ps-config]');
    if (!node) return null;
    const raw = JSON.parse(node.textContent);
    return {
      apiBase: String(raw.apiBase || '').replace(/\/+$/, ''),
      key: String(raw.key || ''),
    };
  }

  function errorCode(status) {
    if (status === 401) return 'not_authorized';
    if (status === 403) return 'revoked';
    return 'unknown';
  }

  /** Asks the server for a one-time sign-in link. Returns the link. */
  async function requestSignInLink(config) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      let response;
      try {
        response = await fetch(`${config.apiBase}/studio/handoff`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${config.key}` },
          cache: 'no-store',
          signal: controller.signal,
        });
      } catch (error) {
        throw new LaunchError('network', error.message);
      }
      if (!response.ok) throw new LaunchError(errorCode(response.status), `HTTP ${response.status}`);

      const data = await response.json();
      // Only ever follow a secure link from the server.
      if (!data || typeof data.url !== 'string' || !data.url.startsWith('https://')) {
        throw new LaunchError('unknown', 'Reply had no sign-in link');
      }
      return data.url;
    } finally {
      clearTimeout(timer);
    }
  }

  function init(root) {
    const button = root.querySelector('[data-ps-open]');
    const errorBox = root.querySelector('[data-ps-error]');
    if (!button) return;

    let config;
    try {
      config = readConfig(root);
    } catch (error) {
      console.error('[photo-studio] Could not read page settings:', error);
      return;
    }
    if (!config || !config.key) return;

    const label = button.textContent;

    // If the customer comes back with the browser's Back button, the page
    // may be restored as it was ("Opening…"), so reset the button.
    window.addEventListener('pageshow', () => {
      button.disabled = false;
      button.textContent = label;
    });

    button.addEventListener('click', async () => {
      button.disabled = true;
      button.textContent = 'Opening…';
      if (errorBox) errorBox.hidden = true;

      try {
        const url = await requestSignInLink(config);
        window.location.assign(url);
        // Keep the button disabled: the page is leaving.
      } catch (error) {
        if (!(error instanceof LaunchError)) console.error('[photo-studio]', error);
        const code = error instanceof LaunchError ? error.code : 'unknown';
        if (errorBox) {
          errorBox.textContent = MESSAGES[code] || MESSAGES.unknown;
          errorBox.hidden = false;
        }
        button.disabled = false;
        button.textContent = label;
      }
    });
  }

  function boot() {
    document.querySelectorAll('[data-ps-root]').forEach((root) => {
      try {
        init(root);
      } catch (error) {
        console.error('[photo-studio] Startup failed:', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
